import { AlertCircle, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useCredits } from '@/hooks/useCredits';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export function CreditWarningBanner() {
  const { balance } = useCredits();
  const [dismissed, setDismissed] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Show banner if balance is below 100 credits and not dismissed
    if (balance && balance.total_credits < 100 && !dismissed) {
      setShow(true);
    } else {
      setShow(false);
    }
  }, [balance, dismissed]);

  // Don't render if not showing
  if (!show) return null;

  const isZero = (balance?.total_credits ?? 0) <= 10;
  const alertClass = isZero ? 'border-red-500 bg-red-50' : 'border-orange-500 bg-orange-50';
  const iconColor = isZero ? 'text-red-600' : 'text-orange-600';
  const textColor = isZero ? 'text-red-800' : 'text-orange-800';

  return (
    <Alert className={`mb-4 ${alertClass} relative`}>
      <AlertCircle className={`h-4 w-4 ${iconColor}`} />
      <AlertDescription className={`${textColor} pr-8`}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <strong className="block mb-1">
              {isZero
                ? '⚠️ Credits Depleted'
                : `⚡ Low Credits: ${balance?.total_credits} remaining`}
            </strong>
            <p className="text-sm">
              {isZero
                ? 'Content generation has been paused. Add credits to resume automated posting.'
                : 'You\'re running low on credits. Add more to ensure uninterrupted content generation.'}
            </p>
          </div>
          <Link to="/settings/billing" className="ml-4 flex-shrink-0">
            <Button
              size="sm"
              variant={isZero ? "destructive" : "default"}
            >
              Add Credits
            </Button>
          </Link>
        </div>
      </AlertDescription>
      <button
        onClick={() => setDismissed(true)}
        className={`absolute top-2 right-2 ${textColor} hover:opacity-70 transition-opacity`}
        aria-label="Dismiss alert"
      >
        <X className="h-4 w-4" />
      </button>
    </Alert>
  );
}